import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { AuthConfig, OAuthModule, OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { appRoutes } from 'app/app.routes';
import { provideAuthI } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { authCodeFlowConfig } from './core/auth/auth.config';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideOAuthClient(),
        provideRouter(appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({scrollPositionRestoration: 'enabled'}),
        ),
        importProvidersFrom(OAuthModule.forRoot()),
        {provide:AuthConfig, useValue: authCodeFlowConfig},
        // Process the OIDC login BEFORE the router runs. The redirect URI is the app root
        // ("/"), and the ""->"dashboard" route redirect drops the "?code=..." query string,
        // so the guard never saw the code to exchange it -> no token -> redirect loop.
        // Running it here (against window.location, independent of the Angular router)
        // exchanges the code, stores the token and cleans the URL before any route/guard runs.
        {
            provide: APP_INITIALIZER,
            multi: true,
            deps: [OAuthService],
            useFactory: (oauth: OAuthService) => () => {
                oauth.configure(authCodeFlowConfig);
                // An OIDC callback always carries ?code=...&state=.... Pages such as /generate-password have a "code"
                // of their own (the encrypted email from the welcome email); tryLogin would take it for an
                // authorization code and strip it from the URL before the page reads it. Without "state", only load
                // the discovery document and leave the URL alone.
                const query = new URLSearchParams(window.location.search);
                const ownCodeNotLogin = query.has('code') && !query.has('state');
                const start = ownCodeNotLogin
                    ? oauth.loadDiscoveryDocument().then(() => true)
                    : oauth.loadDiscoveryDocumentAndTryLogin();
                return start
                    .then(() => {
                        if (oauth.hasValidAccessToken()) {
                            oauth.setupAutomaticSilentRefresh();
                        }
                    })
                    .catch(err => {
                        // Never let a login error block app startup; the guard handles the
                        // unauthenticated case. If the API is https://localhost with an
                        // untrusted dev cert, trust it (dotnet dev-certs https --trust).
                        console.error('[OIDC init] discovery / token exchange failed', err);
                    });
            },
        },
        // provideAuth(
        //     {
        //         config: {
        //             authority: environment.ssourl,
        //             redirectUrl: window.location.origin + '/',
        //             postLogoutRedirectUri: window.location.origin + '/signout-callback-oidc',
        //             clientId: environment.authConfig.clientId,
        //             scope: 'openid profile email offline_access roles',
        //             responseType: 'code',
        //             silentRenew: true,
        //             useRefreshToken: true,
        //         }
        //     },
        //     withAppInitializerAuthCheck()
        // ),
        // Material Date Adapter
        {
            provide : DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide : MAT_DATE_FORMATS,
            useValue: {
                parse  : {
                    dateInput: 'D',
                },
                display: {
                    dateInput         : 'DDD',
                    monthYearLabel    : 'LLL yyyy',
                    dateA11yLabel     : 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Fuse
        provideAuthI(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay   : 0,
                services: mockApiServices,
            },
            fuse   : {
                layout : 'classic',
                scheme : 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme  : 'theme-default',
                themes : [
                    {
                        id  : 'theme-default',
                        name: 'Default',
                    },
                    {
                        id  : 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id  : 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id  : 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id  : 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id  : 'theme-amber',
                        name: 'Amber',
                    },
                    {
                        id  : 'theme-enfinity',
                        name: 'Enfinity'
                    }
                ],
            },
        }),
    ],
};
